/**
 * Qué tilde le corresponde a una burbuja saliente.
 *
 * La trampa es Telegram. Su Bot API **no le informa a un bot** si un mensaje se
 * entregó o se leyó: lo único que sabemos es que Telegram lo aceptó. Dibujarle
 * dos tildes sería inventar una confirmación que nadie dio, y el operador la
 * leería como tal. Por eso el canal manda sobre el dato: aunque llegara un
 * `entrega` avanzado —por datos viejos o por un bug— en Telegram se muestra un
 * solo tilde.
 */

export interface Entrega {
  estado: string;
  cuando: string | null;
  motivo?: string;
}

export interface Tilde {
  /** 0 cuando falló: el fallo no es un grado menos de entrega, es otra cosa. */
  tildes: 0 | 1 | 2;
  tono: 'neutro' | 'leido' | 'error';
  /** Lo que se lee al pasar el mouse. */
  titulo: string;
}

const POR_ESTADO: Record<string, Tilde> = {
  sent: { tildes: 1, tono: 'neutro', titulo: 'Enviado' },
  delivered: { tildes: 2, tono: 'neutro', titulo: 'Entregado' },
  // Mismo número de tildes que «entregado», distinto color: dos indicadores
  // idénticos para dos estados distintos harían el indicador inútil.
  read: { tildes: 2, tono: 'leido', titulo: 'Leído' },
};

export function tildeDe(
  canal: 'whatsapp' | 'telegram',
  entrega: Entrega | undefined,
  role: 'user' | 'assistant' | 'admin' = 'assistant',
): Tilde | null {
  // El tilde dice «te llegó lo que te mandamos». En un mensaje entrante no
  // significa nada.
  if (role === 'user') return null;

  if (canal === 'telegram') {
    return {
      tildes: 1,
      tono: 'neutro',
      titulo: 'Enviado (Telegram no informa entrega ni lectura)',
    };
  }

  if (!entrega) return null;

  if (entrega.estado === 'failed') {
    // El único estado accionable. Se muestra distinto de todo lo demás para que
    // no se confunda con «enviado», que dejaría al operador creyendo que llegó.
    return {
      tildes: 0,
      tono: 'error',
      titulo: entrega.motivo ? `No se pudo entregar — ${entrega.motivo}` : 'No se pudo entregar',
    };
  }

  return POR_ESTADO[entrega.estado] ?? null;
}
